import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  exportString: string
  description: string
  isLoading?: boolean
}

const PlanExportStringDialog = ({
  open,
  onOpenChange,
  exportString,
  description,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!open) {
      setCopied(false)
      return
    }
    const frame = requestAnimationFrame(() => {
      const textarea = textareaRef.current
      if (!textarea) return
      textarea.focus()
      textarea.select()
    })
    return () => cancelAnimationFrame(frame)
  }, [open, exportString])

  const handleCopy = async () => {
    await navigator.clipboard.writeText(exportString)
    setCopied(true)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='max-w-lg'
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {t('dashboard.team.plans.transfer.export-title')}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Textarea
          ref={textareaRef}
          value={isLoading ? '' : exportString}
          readOnly
          rows={8}
          className='break-all font-mono text-xs'
          placeholder={
            isLoading ? t('dashboard.team.plans.export-loading') : undefined
          }
        />
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
          >
            {t('dashboard.team.plans.transfer.close')}
          </Button>
          <Button
            type='button'
            className='min-w-40'
            onClick={handleCopy}
            disabled={isLoading || exportString === ''}
          >
            {copied
              ? t('dashboard.team.plans.transfer.copied')
              : t('dashboard.team.plans.transfer.copy-export-string')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PlanExportStringDialog
